import { lazy } from 'react'

import { ROUTES } from 'routes'

import { FEATURE_SWITCHES_OFF, type FeatureSwitches } from 'constants/featureSwitches'

import { CUSTOMER_DOCS_CATALOG, customerDocsCatalogFor } from './index'
import { CUSTOMER_DOCS_LABEL } from './surface'

import type { DocsSource } from './source'

/**
 * The CUSTOMER docs source. `INTERNAL_DOCS` deliberately lives in its own module
 * (`sources.internal.ts`) — see the note there.
 *
 * `Content` is a dynamic import on purpose: it is the module boundary that keeps guide
 * PROSE out of the eager bundle.
 *
 * That boundary is not sufficient on its own, which is what this file originally got
 * wrong. It claimed to be "free of guide text" because both `Content` entries were lazy —
 * but importing `INTERNAL_DOCS_CATALOG` here pulled the internal MANIFEST (titles,
 * excerpts, heading text) into the eager graph, and excerpts are guide text. Splitting a
 * body out means nothing if the summary of that body rides along.
 */

export const CUSTOMER_DOCS: DocsSource = {
  ...CUSTOMER_DOCS_CATALOG,
  id: 'customer',
  label: CUSTOMER_DOCS_LABEL,
  indexPath: ROUTES.DOCS,
  docPath: ROUTES.DOC,
  Content: lazy(() => import('pages/support/DocPage/DocContent')),
  // /docs renders the `overview` guide rather than a grid of cards. See the field's note
  // in source.ts for why the card index was a second copy of the nav.
  overviewSlug: 'overview',
}

/**
 * VRG-680 — `CUSTOMER_DOCS` with launch-switch gating applied (see `customerDocsCatalogFor`).
 * Same `Content` and routes; only the catalog differs. Stable per switch state. Read it through
 * `useCustomerDocs()` rather than calling this with hand-built switches.
 */
const SOURCE_BY_CATALOG = new WeakMap<object, DocsSource>()
export const customerDocsFor = (switches: Partial<FeatureSwitches> = FEATURE_SWITCHES_OFF): DocsSource => {
  const catalog = customerDocsCatalogFor(switches)
  if (catalog === CUSTOMER_DOCS_CATALOG) return CUSTOMER_DOCS
  let source = SOURCE_BY_CATALOG.get(catalog)
  if (!source) {
    source = { ...CUSTOMER_DOCS, ...catalog }
    SOURCE_BY_CATALOG.set(catalog, source)
  }
  return source
}
